import { format } from "rut.js";
import { columns } from '../../../dtos/columns';
import { MutuoBandeja } from "../models/mutuoBandeja";
import { AccionesBandejaMutuo, AccionesContextMenuBandejaMutuo } from "../components/accionesBandejaMutuo";
import { NumericFormat, numericFormatter } from "react-number-format";
import { FiltroBandeja } from "../models/filtroBandeja";
import { TablaBasicFilter } from "../../../utils/tableBasicFilter";
import { EnumEstadoMutuo } from "../../../utils/enums";
import { controlProps } from "../../../components/ui/tablaServer";
import { Checkbox } from "@mui/material";
import { MutuoSeleccionado } from "../models/MutuoSeleccionado";

interface IProps {
    control: any;
    filtros: FiltroBandeja | TablaBasicFilter;
    controlTabla?: any;
    mutuosToActivate: MutuoSeleccionado[];
    setMutuosToActivate: React.Dispatch<React.SetStateAction<MutuoSeleccionado[]>>;
    refresh: () => void;
}

const normalizeEstadoMutuo = (estado?: string | null) =>
    (estado ?? '')
        .trim()
        .toUpperCase()
        .replace(/\s+/g, '_');

const ESTADOS_CON_ACCIONES = new Set([
    normalizeEstadoMutuo(EnumEstadoMutuo.Por_Ingresar),
    normalizeEstadoMutuo(EnumEstadoMutuo.Aprobado),
    normalizeEstadoMutuo(EnumEstadoMutuo.Ingresado),
]);

const canShowActionsForEstado = (estado?: string | null) =>
    ESTADOS_CON_ACCIONES.has(normalizeEstadoMutuo(estado));

export function useColumns({ control, filtros, controlTabla, mutuosToActivate, setMutuosToActivate, refresh }: IProps) {

    const COLUMNS: columns<MutuoBandeja>[] = [
        {
            name: 'Acciones',
            selector: (row: MutuoBandeja) => '',
            cell: (row: MutuoBandeja) => {
                const canShowActions = canShowActionsForEstado(row.estado);

                return canShowActions ? 
                    <AccionesBandejaMutuo
                        mutuo={row}
                        control={control}
                        filtros={filtros}
                        controlTabla={controlTabla} 
                        refresh={refresh}
                    /> : "";
            },
            allowOverflow: true,
            button: true,
            export: false,
        },
        {
            name: 'N° Solicitud',
            selector: (row: MutuoBandeja) => row.numerosolicitud,
            sortable: true,
            sortField: 'numerosolicitud',
            wrap: true,
            alling: 'left',
            export: true,
        },
        {
            name: 'Tipo mutuo',
            selector: (row: MutuoBandeja) => row.tipoMutuo,
            sortable: true,
            sortField: 'tipoMutuo',
            wrap: true,
            export: true,
            alling: 'left',
        },
        {
            name: 'Nombre o razón social',
            selector: (row: MutuoBandeja) => row.deudor,
            sortable: true,
            sortField: 'deudor',
            wrap: true,
            export: true,
            alling: 'left',
        },
        {
            name: 'RUT',
            selector: (row: MutuoBandeja) =>
                format(row.rutDeudor),
            sortable: true,
            sortField: 'rutDeudor',
            wrap: true,
            export: true,
            alling: 'center',
        },
        {
            name: 'Tipo venta',
            selector: (row: MutuoBandeja) => row.tipoVenta,
            sortable: true,
            sortField: 'tipoVenta',
            wrap: true,
            export: true,
        },
        {
            name: 'Tipo crédito',
            selector: (row: MutuoBandeja) => row.tipoCredito,
            sortable: true,
            sortField: 'tipoCredito',
            wrap: true,
            export: true,

        },
        {
            name: 'Plazo',
            selector: (row: MutuoBandeja) => row.plazocredito,
            sortable: true,
            sortField: 'plazocredito',
            wrap: true,
            export: true,

        },
        {
            name: 'Monto',
            selector: (row: MutuoBandeja) => row.montocredito,
            cell: (row: MutuoBandeja) => numericFormatter(row.montocredito.toString(), { "thousandsGroupStyle": "thousand", "thousandSeparator": ".", "decimalSeparator": ",", "prefix": "UF " }),
            sortable: true,
            sortField: 'montocredito',
            wrap: true,
            right: true,
            export: true,
        },
        {
            name: 'Tasa',
            selector: (row: MutuoBandeja) => row.tasaemision,
            cell: (row: MutuoBandeja) => numericFormatter(row.tasaemision.toString(), { "thousandsGroupStyle": "thousand", "thousandSeparator": ".", "decimalSeparator": ",", "suffix": "%" }),
            sortable: true,
            sortField: 'tasaemision',
            wrap: true,
            alling: 'center',
            export: true,
        },
        {
            name: 'Estado',
            selector: (row: MutuoBandeja) => row.estado,
            sortable: false,
            sortField: 'estado',
            wrap: true,
            export: true,

        },
        {
            name: 'Responsable',
            selector: (row: MutuoBandeja) => row.responsable,
            sortable: false,
            sortField: 'responsable',
            wrap: true,
            export: true,
            alling: 'center'
        },
        {
            name: 'Ejecutivo comercial',
            selector: (row: MutuoBandeja) => row.ejecutivoComercial,
            sortable: true,
            sortField: 'ejecutivoComercial',
            wrap: true,
            export: true,
        },
        {
            name: 'Fecha firma primera escritura',
            selector: (row: MutuoBandeja) => {
                if( !row.fechaFirmaPrimeraEscritura ) return '';
                const fecha = new Date(row.fechaFirmaPrimeraEscritura);
                const dia = String(fecha.getDate()).padStart(2, '0');
                const mes = String(fecha.getMonth() + 1).padStart(2, '0');
                const anio = fecha.getFullYear();
                return `${dia}-${mes}-${anio}`;
            },
            sortable: true,
            sortField: 'fechaFirmaPrimeraEscritura',
            wrap: true,
            export: true,
        },

    ]

    if( (filtros as FiltroBandeja).estadoMutuo === 'APROBADO' )
        COLUMNS.unshift({
            name: (
                <Checkbox
                    size="small"
                    checked={
                        controlTabla?.data?.length > 0 &&
                        mutuosToActivate.length === controlTabla.data.length
                    }
                    onChange={(e) => {
                        if (e.target.checked && controlTabla?.data) {
                            const allObjects = controlTabla.data.map((item: MutuoBandeja) => ({
                                id: item.id,
                                numeroSolicitud: item.numerosolicitud,
                                tipoMutuo: item.tipoMutuo,
                                nombre: item.deudor, // o la razón social si usas otro campo
                                rut: item.rutDeudor,
                                observacion: "", // valor inicial vacío
                            }));
                            setMutuosToActivate(allObjects);
                        } else {
                            setMutuosToActivate([]);
                        }
                    }}
                />
            ),
            selector: (row: MutuoBandeja) => row.id,
            cell: (row: MutuoBandeja) => (
                <Checkbox
                    size="small"
                    checked={mutuosToActivate.some((m) => m.id === row.id)}
                    onChange={(e) => {
                        setMutuosToActivate((prev) => {
                        if (e.target.checked) {
                            // Agregar si no está
                            return [
                                ...prev,
                                {
                                    id: row.id,
                                    numeroSolicitud: row.numerosolicitud,
                                    tipoMutuo: row.tipoMutuo,
                                    nombre: row.deudor,
                                    rut: row.rutDeudor,
                                    observacion: "",
                                },
                            ];
                        } else {
                            return prev.filter((m) => m.id !== row.id);
                        }
                        });
                    }}
                />
            ),
            allowOverflow: true,
            button: true,
            export: false,
            width: '60px',
        });

    const contextMenuRender = (row: MutuoBandeja, handleClose: () => void) => {
    const canShowActions = canShowActionsForEstado(row.estado);

    return canShowActions ?
        <AccionesContextMenuBandejaMutuo
            mutuo={row}
            control={control}
            filtros={filtros}
            controlTabla={controlTabla}
            refresh={refresh}
            handleClose={handleClose}
        /> : null;
    }

    return { COLUMNS, contextMenuRender };

}